import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getVerificationStatus, resendVerification } from '../../services/adminService';
import { VerificationStatus } from '../../types/admin';
import './VerificationModal.css';

interface VerificationModalProps {
	userId: string;
	userEmail: string;
	userName: string;
	isOpen: boolean;
	onClose: () => void;
	onVerificationSent?: () => void;
}

export const VerificationModal: React.FC<VerificationModalProps> = ({ userId, userEmail, userName, isOpen, onClose, onVerificationSent }) => {
	const { token } = useAuth();
	const [verificationStatus, setVerificationStatus] = useState<VerificationStatus | null>(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string>('');
	const [sendingVerification, setSendingVerification] = useState(false);

	const loadVerificationStatus = useCallback(async () => {
		if (!token) return;

		try {
			setLoading(true);
			setError('');
			const status = await getVerificationStatus(token, userId);
			setVerificationStatus(status);
		} catch (err) {
			setError('Greška pri učitavanju statusa verifikacije');
			console.error('Load verification status error:', err);
		} finally {
			setLoading(false);
		}
	}, [token, userId]);

	useEffect(() => {
		if (isOpen && token) {
			loadVerificationStatus();
		}
	}, [isOpen, token, loadVerificationStatus]);

	const handleResendVerification = async () => {
		if (!token) return;

		try {
			setSendingVerification(true);
			setError('');
			const response = await resendVerification(token, userId);

			// Refresh status after sending
			await loadVerificationStatus();

			if (onVerificationSent) {
				onVerificationSent();
			}

			alert(response.message || 'Verifikacioni email je uspešno poslat');
		} catch (err) {
			setError('Greška pri slanju verifikacionog email-a');
			console.error('Resend verification error:', err);
		} finally {
			setSendingVerification(false);
		}
	};

	const formatDate = (dateString: string) => {
		return new Date(dateString).toLocaleDateString('sr-RS', {
			year: 'numeric',
			month: '2-digit',
			day: '2-digit',
			hour: '2-digit',
			minute: '2-digit',
		});
	};

	if (!isOpen) return null;

	return (
		<div className='verification-modal-overlay'>
			<div className='verification-modal'>
				<div className='verification-modal-header'>
					<h3>Verifikacija email adrese</h3>
					<button onClick={onClose} className='close-btn'>
						×
					</button>
				</div>

				<div className='verification-modal-content'>
					<div className='user-info'>
						<h4>{userName}</h4>
						<p className='user-email'>{userEmail}</p>
					</div>

					{error && <div className='error-message'>{error}</div>}

					{loading ? (
						<div className='loading'>
							<p>Učitavanje statusa verifikacije...</p>
						</div>
					) : verificationStatus ? (
						<div className='verification-status'>
							<div className='status-grid'>
								<div className='status-item'>
									<label>Email verifikovan:</label>
									<span className={`status-value ${verificationStatus.emailVerified ? 'verified' : 'unverified'}`}>{verificationStatus.emailVerified ? '✅ Da' : '❌ Ne'}</span>
								</div>

								<div className='status-item'>
									<label>Lozinka postavljena:</label>
									<span className={`status-value ${verificationStatus.passwordSet ? 'verified' : 'unverified'}`}>{verificationStatus.passwordSet ? '✅ Da' : '❌ Ne'}</span>
								</div>

								<div className='status-item'>
									<label>Aktivan verifikacioni token:</label>
									<span className={`status-value ${verificationStatus.hasVerificationToken ? 'verified' : 'unverified'}`}>{verificationStatus.hasVerificationToken ? '✅ Da' : '❌ Ne'}</span>
								</div>

								{verificationStatus.tokenExpires && (
									<div className='status-item'>
										<label>Token ističe:</label>
										<span className='status-value'>{formatDate(verificationStatus.tokenExpires)}</span>
									</div>
								)}
							</div>

							{!verificationStatus.emailVerified && (
								<div className='verification-actions'>
									<div className='action-info'>
										<p>{verificationStatus.hasVerificationToken ? 'Korisnik već ima aktivan verifikacioni token. Možete poslati novi email sa istim token-om ili kreirati novi token.' : 'Korisnik nema aktivan verifikacioni token. Potrebno je poslati verifikacioni email.'}</p>
									</div>

									<button onClick={handleResendVerification} disabled={sendingVerification} className='verification-btn verification-btn-primary'>
										{sendingVerification ? 'Šalje se...' : verificationStatus.hasVerificationToken ? 'Pošalji verifikaciju ponovo' : 'Pošalji verifikacioni email'}
									</button>
								</div>
							)}

							{verificationStatus.emailVerified && (
								<div className='success-message'>
									<p>✅ Email je uspešno verifikovan i korisnik može da se prijavi na sistem.</p>
								</div>
							)}
						</div>
					) : null}
				</div>

				<div className='verification-modal-footer'>
					<button onClick={onClose} className='verification-btn verification-btn-secondary'>
						Zatvori
					</button>
				</div>
			</div>
		</div>
	);
};
